import { isValidDate } from "Validation/validator";

// Works for both YY and YYYY format assuming YY always refers to 20YY
export function IsLeapYear(year) {
    // Every 4th year except 100s, and every 400th year
    return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

// Values for converting .NET ticks to/from universal ticks
// https://stackoverflow.com/questions/7966559/how-to-convert-javascript-date-object-to-ticks
const EPOCH_TICKS = 621355968000000000;
const TICKS_PER_MS = 10000;

// Make sure the dotnet ticks are in utc time
export function getDateFromDotnetTicks(ticksUtc) {
    return new Date((ticksUtc - EPOCH_TICKS) / TICKS_PER_MS);
}

export function convertJavascriptTicksToDotnetTicks(javascriptTicks) {
    return javascriptTicks * TICKS_PER_MS + EPOCH_TICKS;
}

// getDateFields handles localization of dates
export function getDateFields($app, value) {
    const dateSeparator = $app.DateCultureMessageInfo.DateSeparator;
    const dateStr = $app.DateCultureMessageInfo.ShortDateFormat.split(dateSeparator);
    let yrIdx, moIdx, dayIdx;

    // preserving status quo date format for en-US/en-SV locale
    switch ($app.DateCultureMessageInfo.Locale) {
        case "en-US":
        case "es-SV":
            yrIdx = dateStr.indexOf("yy");
            moIdx = dateStr.indexOf("m");
            dayIdx = dateStr.indexOf("d");
            break;
        default:
            yrIdx = dateStr.indexOf("yy");
            moIdx = dateStr.indexOf("mm");
            dayIdx = dateStr.indexOf("dd");
    }

    const dateFields = value.split(dateSeparator).map(function (field) {
        return parseInt(field, 10);
    });

    if (!Number.isInteger(dateFields[yrIdx])) {
        dateFields[yrIdx] = new Date().getFullYear();
    } else if (dateFields[yrIdx] < 100) {
        // Adjust two-digit year
        dateFields[yrIdx] += 2000;
        if (
            $app.DateCultureMessageInfo.TwoDigitYearMax &&
            dateFields[yrIdx] > $app.DateCultureMessageInfo.TwoDigitYearMax
        ) {
            dateFields[yrIdx] -= 100;
        }
    }

    return [dateFields[yrIdx], dateFields[moIdx], dateFields[dayIdx]];
}

export function dateStrToClientDate($app, dateStr) {
    if (!dateStr) {
        return null;
    }
    const [year, month, day] = getDateFields($app, dateStr);
    if (isValidDate($app, null, year, month, day)) {
        return new ClientDate($app, year, month, day);
    }
    return null;
}

export function dateStrToServerObject($app, dateStr) {
    const clientDate = dateStrToClientDate($app, dateStr);
    if (clientDate) {
        return {
            Year: clientDate.Year,
            Month: clientDate.Month,
            Day: clientDate.Day
        };
    }
    return null;
}

/**
 * To be used with Array.sort for sorting a list of clientDates
 * @param {ClientDate} cd1 First Date
 * @param {ClientDate} cd2 Second Date
 * @param {Boolean} isDescending Optional - if specified, sort will be in descending order
 */
export function dateCompare(cd1, cd2, isDescending) {
    if (!cd1 || !cd2) {
        throw "Unexpected null date in dateCompare";
    }

    // cd1 strictly before cd2 - cd1 comes second if descending, first otherwise
    if (cd1.isBefore(cd2, true)) {
        return isDescending ? 1 : -1;
    } else if (cd1.equals(cd2)) {
        return 0;
    }
    // cd1 strictly after cd2 - cd1 first if descending, second otherwise
    else {
        return isDescending ? -1 : 1;
    }
}

/**
 * Determines if a date should be Time Off based on the recurring rule
 * @param {Object} rule ClientRecurringRule object
 * @param {ClientDate} clientDate Date to check against the rule
 * @returns True if the clientDate is a valid instance of the rule, false otherwise
 */
export function isRecurringEvent(rule, clientDate) {
    if (clientDate.isBefore(rule.LastUpdate)) {
        return false;
    }

    if (rule.DateOfYear != null) {
        return rule.DateOfYear.Month === clientDate.Month && rule.DateOfYear.Day === clientDate.Day;
    }

    if (clientDate.Month !== rule.ByMonth) {
        return false;
    }

    return clientDate.isNthWeekdayInMonth(rule.ByWeek, rule.ByDay);
}

/**
 * Calculates the number of non-business days between two dates.
 * @param {Object} $app The app object, used for locale information
 * @param {ClientDate} start Starting date, must be before or equal to end date
 * @param {ClientDate} end Ending date, must be after or equal to start date
 * @param {Object} businessCalendar A ClientBusinessCalendar object
 * @returns The integer number of non-business days betweeen the two dates.
 */
export function numberNonBusinessDaysBetween($app, start, end, businessCalendar) {
    if (
        !isValidDate($app, null, start.Year, start.Month, start.Day) ||
        !isValidDate($app, null, end.Year, end.Month, end.Day)
    ) {
        throw new Error("Unable to calculate non-business days, invalid date(s)");
    }

    let count = 0;
    let tempDate = start;
    const endDayOfWeek = end.getDayOfWeek();

    // Count number of business days in week
    const businessDaysInWeek = businessCalendar.BusinessWeek.reduce((accumulator, value) => {
        return accumulator + (value ? 1 : 0);
    }, 0);

    // Count number of non-business days in the partial week
    while (tempDate.getDayOfWeek() !== endDayOfWeek) {
        if (!businessCalendar.BusinessWeek[tempDate.getDayOfWeek()]) {
            count++;
        }
        tempDate = tempDate.addDays(1);
    }

    // Calculate number of non-business days in the full weeks
    const differenceMs = new Date(end.Year, end.Month - 1, end.Day) - new Date(start.Year, start.Month - 1, start.Day);
    const numDays = Math.floor(differenceMs / 86400000) + 1 - count;
    let numWeeks = Math.floor(numDays / 7);
    numWeeks = numWeeks >= 0 ? numWeeks : 0;
    const nonBusinessDays = numWeeks * (7 - businessDaysInWeek);

    // Add the number of non-business days that are marked as time off
    count += businessCalendar.TimeOff.reduce((accumulator, timeOff) => {
        return (
            accumulator +
            (start.isBefore(timeOff) &&
            end.isAfter(timeOff, true) &&
            businessCalendar.BusinessWeek[ClientDate.getDayOfWeekStatic(timeOff)]
                ? 1
                : 0)
        );
    }, 0);

    // Add the number of non-business days from recurring rules (that aren't already covered by time-off)
    const applicableRecurringRules = [];
    let minUpdateDate = end;
    for (let i = 0; i < businessCalendar.RecurringRules.length; i++) {
        const rule = businessCalendar.RecurringRules[i];
        if (end.isAfter(rule.LastUpdate, true)) {
            applicableRecurringRules.push(rule);
            // rule.LastUpdate will be ClientDate shaped, but doesn't have methods
            if (ClientDate.isBeforeStatic(rule.LastUpdate, minUpdateDate, true)) {
                minUpdateDate = rule.LastUpdate;
            }
        }
    }

    if (applicableRecurringRules.length > 0) {
        // minUpdateDate may be a ClientDate shape from server, ensure iterationDate has methods
        let iterationDate = ClientDate.isBeforeStatic(minUpdateDate, start, true)
            ? start
            : new ClientDate($app, minUpdateDate.Year, minUpdateDate.Month, minUpdateDate.Day);
        while (iterationDate.isBefore(end, true)) {
            if (businessCalendar.BusinessWeek[iterationDate.getDayOfWeek()]) {
                for (let i = 0; i < applicableRecurringRules.length; i++) {
                    const rule = applicableRecurringRules[i];
                    if (isRecurringEvent(rule, iterationDate)) {
                        count++;
                        break;
                    }
                }
            }
            iterationDate = iterationDate.addDays(1);
        }
    }

    return nonBusinessDays + count;
}

export class ClientDate {
    #$app; // Private field must be declared

    constructor($app, year, month, day) {
        // If constructor called without arguments, create a date
        // representing locale now by wrapping the native Date object
        if (year == null && month == null && day == null) {
            const now = new Date();
            year = now.getFullYear();
            month = now.getMonth() + 1; // JS native Date uses 0-indexed months
            day = now.getDate();
        }

        this.Year = year;
        this.Month = month;
        this.Day = day;
        this.#$app = $app;

        this.Locale = $app.DateCultureMessageInfo.Locale;
    }

    /**
     * Initialize an instance of this ClientDate from a ClientDate-shaped object produced by
     * the server
     * @param {ClientDate} clientDate
     */
    static fromServerObject($app, clientDate) {
        return new ClientDate($app, clientDate.Year, clientDate.Month, clientDate.Day);
    }

    /**
     * Initialize an instance of ClientDate from a native JS date
     * @param {Date} nativeDate
     */
    static fromNativeDate($app, nativeDate) {
        return new ClientDate($app, nativeDate.getFullYear(), nativeDate.getMonth() + 1, nativeDate.getDate());
    }

    addDays(nDays) {
        const nativeDate = new Date(this.Year, this.Month - 1, this.Day);

        // Use this.Year, rather than nativeDate.getFullYear because native date
        // constructor maps year 0-99 -> 1900-1999
        // setFullYear correctly updates the month/year if the number of days added causes
        // the month/year to increment.
        nativeDate.setFullYear(this.Year, nativeDate.getMonth(), nativeDate.getDate() + nDays);
        return new ClientDate(this.#$app, nativeDate.getFullYear(), nativeDate.getMonth() + 1, nativeDate.getDate());
    }

    // Adding or subtracting months increments or decrements the numeric value of the
    // month by the specified number of months while trying to keep the numeric date value
    // the same if possible. If this is not possible, for instance, "1 month after 1/31",
    // we round down to the last valid date in the numerically-expected month. So "1 month
    // after 1/31" would be 2/28 in a regular year or 2/29 in a leap year. Sadly the JS
    // setMonth function doesn't exhibit this behavior
    addMonths(nMonths) {
        // Convert month to 0-indexed to make math easier
        const currentMonth = this.Month - 1; // [0, 11]
        let expectedMonth = currentMonth + nMonths;

        // Any negative number will decrement year by at least 1, only positive numbers >= 12 will do so
        const expectedYear = this.Year + Math.floor(expectedMonth / 12);

        // Now that we've calculated our expected year based on the absolute value of nMonths,
        // reduce it to the range [-11, 11]
        if (expectedMonth > 11 || expectedMonth < -11) {
            expectedMonth %= 12;
        }

        // Now take care of negative months by adding 12 (again, we've already figured out the expected
        // year)
        if (expectedMonth < 0) {
            // January == 0, January-1 == -1, -1 + 12 == 11 => December
            expectedMonth += 12;
        }

        const isLeapYear = IsLeapYear(expectedYear);
        const lastValidDate = this.getLastDateInMonth(expectedMonth, isLeapYear);

        const expectedDate = this.Day <= lastValidDate ? this.Day : lastValidDate;

        return new ClientDate(this.#$app, expectedYear, expectedMonth + 1, expectedDate);
    }

    getLastDateInMonth(monthIndex, isLeapYear) {
        const lastDatesInMonth = [
            31, // Jan
            isLeapYear ? 29 : 28, // Feb
            31, // Mar
            30, // Apr
            31, // May
            30, // Jun
            31, // Jul
            31, // Aug
            30, // Sept
            31, // Oct
            30, // Nov,
            31 // Dec
        ];
        return lastDatesInMonth[monthIndex];
    }

    addYears(nYears) {
        // Special handling for moving from leap day to a year in which it doesn't exist
        // Matches behavior of DateTime.addYears for a leap day
        if (this.Month === 2 && this.Day === 29) {
            const destYear = this.Year + nYears;
            if (!IsLeapYear(destYear)) {
                return new ClientDate(this.#$app, destYear, this.Month, 28);
            }
        }
        return new ClientDate(this.#$app, this.Year + nYears, this.Month, this.Day);
    }

    equals(otherDate) {
        return this.Year === otherDate.Year && this.Month === otherDate.Month && this.Day === otherDate.Day;
    }

    isOnOrAfter(otherDate) {
        return this.isAfter(otherDate, false);
    }

    isAfter(otherDate, strictlyAfter) {
        return ClientDate.isAfterStatic(this, otherDate, strictlyAfter);
    }

    isBefore(otherDate, strictlyBefore) {
        return !this.isAfter(otherDate, !strictlyBefore);
    }

    static isAfterStatic(clientDate1, clientDate2, strictlyAfter) {
        if (clientDate1.Year > clientDate2.Year) {
            return true;
        }
        if (clientDate1.Year === clientDate2.Year && clientDate1.Month > clientDate2.Month) {
            return true;
        }
        if (clientDate1.Year === clientDate2.Year && clientDate1.Month === clientDate2.Month) {
            if ((clientDate1.Day >= clientDate2.Day && !strictlyAfter) || clientDate1.Day > clientDate2.Day) {
                return true;
            }
        }

        return false;
    }

    static isBeforeStatic(clientDate1, clientDate2, strictlyBefore) {
        return !ClientDate.isAfterStatic(clientDate1, clientDate2, !strictlyBefore);
    }

    getDayOfWeek() {
        return ClientDate.getDayOfWeekStatic(this);
    }

    static getDayOfWeekStatic(clientDate) {
        const nativeDate = new Date(clientDate.Year, clientDate.Month - 1, clientDate.Day);
        return nativeDate.getDay();
    }

    getWeekOfMonth() {
        // Day 1-7 == Week 1,
        // Day 8-14 == Week 2,
        // ...
        // Day 29-x == Week 5
        return Math.floor((this.Day - 1) / 7) + 1;
    }

    isNthWeekdayInMonth(n, dayOfWeek) {
        if (this.getDayOfWeek() !== dayOfWeek) {
            return false;
        }

        const weekOfMonth = this.getWeekOfMonth();
        if (n > 0) {
            return weekOfMonth === n;
        }

        // Start at first of the month
        const searchDate = new Date(this.Year, this.Month - 1, 1);
        let dayOfWeekOffset = dayOfWeek - searchDate.getDay();
        if (dayOfWeekOffset < 0) {
            dayOfWeekOffset += 7;
        }

        // Set to the first of given weekday in month
        searchDate.setFullYear(searchDate.getFullYear(), searchDate.getMonth(), searchDate.getDate() + dayOfWeekOffset);
        let numWeekdayInMonth = 0;
        while (searchDate.getMonth() === this.Month - 1) {
            numWeekdayInMonth++;
            searchDate.setFullYear(searchDate.getFullYear(), searchDate.getMonth(), searchDate.getDate() + 7);
        }

        // if n == -1, we want last instance in month. If 5 of day in month,
        // want the 5th one. this.getWeekOfMonth will return 5 on that date
        return numWeekdayInMonth + n + 1 === weekOfMonth;
    }

    // order to format a date in a given locale, first need to construct an actual date.
    toString() {
        // months in javascript are zero based, days and months are not.  go figure.
        const date = new Date(this.Year, this.Month - 1, this.Day, 0, 0, 0);
        return date.toLocaleString(this.Locale, { day: "numeric", month: "numeric", year: "numeric" });
    }
}
